import 'regenerator-runtime/runtime';
import React, { useState, useEffect, useRef, useCallback } from 'react';

import { Menu, LogIn, X, List, Map as MapIcon, Package, Truck, ShieldCheck, Loader2, CheckSquare, Square, HelpCircle, Power } from 'lucide-react';

import { collection, onSnapshot, query, doc, getDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { db, auth } from './firebase';
import './App.css';

import SupportForm from './components/SupportForm';
import AdminPanel from './AdminPanel';
import TermsModal from './TermsModal';
import AuthWizard from './AuthWizard';
import Profile from './Profile';
import AdBanner from './AdBanner';

// --- מודולי המשלוחים ---
import DeliveryRequestForm from './DeliveryRequestForm';
import CourierJobList from './CourierJobList';
import ActiveDelivery from './ActiveDelivery';
import { listenToMyActiveDelivery, listenToOpenDeliveries, DELIVERY_STATUS } from './deliveryService';
import { startBroadcasting, stopBroadcasting } from './driverLocationService';

export default function App() {
  const [user, setUser] = useState(null);
  const [appState, setAppState] = useState('welcome');

  // תקנון
  const [isTermsAgreed, setIsTermsAgreed] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [showSupport, setShowSupport] = useState(false);

  // --- מצב נוסע/שליח ---
  const [mode, setMode] = useState('sender'); // 'sender' | 'courier'
  const [isOnline, setIsOnline] = useState(false);

  // משלוחים
  const [openDeliveries, setOpenDeliveries] = useState([]); // לצד השליח (ציור על מפה)
  const [myActiveDelivery, setMyActiveDelivery] = useState(null); // משלוח פעיל של השולח
  const [trackingDeliveryId, setTrackingDeliveryId] = useState(null); // המשלוח שהשליח לקח
  const [trackingRole, setTrackingRole] = useState('sender');
  const [courierLiveLocation, setCourierLiveLocation] = useState(null); // לציור על מפת השולח

  // מפה ומיקום
  const [googleLoaded, setGoogleLoaded] = useState(false);
  const mapRef = useRef(null);
  const googleMap = useRef(null);
  const markersRef = useRef([]);
  const polylinesRef = useRef([]);
  const courierMarkerRef = useRef(null);

  const [mapCenter] = useState({ lat: 31.7517, lng: 35.0844 }); // בית שמש כברירת מחדל
  const [userLocation, setUserLocation] = useState(null);

  // --- ציור מרקרים של משלוחים פתוחים (במצב שליח) ---
  const renderMarkers = useCallback(() => {
    if (!googleMap.current || !window.google) return;

    markersRef.current.forEach((m) => m.setMap && m.setMap(null));
    markersRef.current = [];
    polylinesRef.current.forEach((p) => p.setMap && p.setMap(null));
    polylinesRef.current = [];

    // מציירים נקודות איסוף/מסירה רק כשהשליח מקוון ובמצב שליח
    if (mode !== 'courier' || !isOnline) return;

    openDeliveries.forEach((d) => {
      if (!d.pickup?.lat || !d.dropoff?.lat) return;
      const pickup = { lat: d.pickup.lat, lng: d.pickup.lng };
      const dropoff = { lat: d.dropoff.lat, lng: d.dropoff.lng };

      const line = new window.google.maps.Polyline({
        path: [pickup, dropoff], geodesic: true, strokeColor: '#3b82f6', strokeOpacity: 0.7, strokeWeight: 3,
      });
      line.setMap(googleMap.current);
      polylinesRef.current.push(line);

      const pickMarker = new window.google.maps.Marker({
        position: pickup, map: googleMap.current,
        icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 7, fillColor: '#10b981', fillOpacity: 1, strokeColor: 'white', strokeWeight: 2 },
        label: { text: '₪' + d.fare, color: '#065f46', fontWeight: 'bold', fontSize: '11px' },
      });
      pickMarker.addListener('click', () => { setAppState('courier-list'); });
      markersRef.current.push(pickMarker);

      const dropMarker = new window.google.maps.Marker({
        position: dropoff, map: googleMap.current,
        icon: { path: 'M -5 -5 L 5 -5 L 5 5 L -5 5 Z', scale: 1.2, fillColor: '#ef4444', fillOpacity: 1, strokeColor: 'white', strokeWeight: 2 },
      });
      markersRef.current.push(dropMarker);
    });
  }, [openDeliveries, mode, isOnline]);

  // --- ציור מיקום חי של השליח על מפת השולח ---
  useEffect(() => {
    if (!googleMap.current || !window.google) return;
    if (courierLiveLocation && trackingRole === 'sender') {
      const pos = { lat: courierLiveLocation.lat, lng: courierLiveLocation.lng };
      if (!courierMarkerRef.current) {
        courierMarkerRef.current = new window.google.maps.Marker({
          position: pos, map: googleMap.current,
          icon: { path: 'M -2 -4 L 2 -4 L 3 0 L 2 4 L -2 4 L -3 0 Z', scale: 3, rotation: courierLiveLocation.heading || 0,
                  fillColor: '#000', fillOpacity: 1, strokeColor: 'white', strokeWeight: 1.5 },
          zIndex: 999,
        });
      } else {
        courierMarkerRef.current.setPosition(pos);
      }
      googleMap.current.panTo(pos);
    } else if (courierMarkerRef.current) {
      courierMarkerRef.current.setMap(null);
      courierMarkerRef.current = null;
    }
  }, [courierLiveLocation, trackingRole]);

  // --- כניסה/יציאה ---
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          const snap = await getDoc(doc(db, "users", currentUser.uid));
          if (snap.exists()) {
            setUser({ uid: currentUser.uid, ...snap.data() });
            setAppState('intro');
          }
        } catch (e) { console.error(e); }
      } else {
        setUser(null);
        setAppState('welcome');
      }
      setInitializing(false);
    });
    return () => unsub();
  }, []);

  // --- טעינת מפות ---
  useEffect(() => {
    const checkGoogle = setInterval(() => {
      if (window.google?.maps?.Map && mapRef.current && !googleMap.current) {
        clearInterval(checkGoogle);
        try {
          googleMap.current = new window.google.maps.Map(mapRef.current, {
            center: mapCenter, zoom: 13, disableDefaultUI: true, clickableIcons: false,
            styles: [
              { featureType: "poi", stylers: [{ visibility: "off" }] },
              { featureType: "transit", stylers: [{ visibility: "off" }] },
            ],
          });
          setGoogleLoaded(true);
        } catch (e) { console.error(e); }
      }
    }, 500);

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        (err) => console.log(err)
      );
    }
    return () => clearInterval(checkGoogle);
  }, [mapCenter]);

  // --- מאזין למשלוח הפעיל של השולח ---
  useEffect(() => {
    if (!user || mode !== 'sender') { setMyActiveDelivery(null); return; }
    const unsub = listenToMyActiveDelivery(user.uid, (d) => {
      setMyActiveDelivery(d);
      // כשמשלוח נתפס — פותחים אוטומטית מסך מעקב
      if (d && (d.status === DELIVERY_STATUS.ACCEPTED || d.status === DELIVERY_STATUS.PICKED_UP)) {
        setTrackingDeliveryId(d.id);
        setTrackingRole('sender');
      }
    });
    return () => unsub();
  }, [user, mode]);

  // --- מאזין למשלוחים פתוחים (לציור על מפת השליח) ---
  useEffect(() => {
    if (mode !== 'courier' || !isOnline) { setOpenDeliveries([]); return; }
    const unsub = listenToOpenDeliveries(setOpenDeliveries);
    return () => unsub();
  }, [mode, isOnline]);

  // --- שידור מיקום השליח ---
  useEffect(() => {
    if (!user) return;
    if (mode === 'courier' && isOnline) {
      startBroadcasting(user.uid, { status: trackingDeliveryId ? 'on_trip' : 'online', deliveryId: trackingDeliveryId });
    } else {
      stopBroadcasting(user.uid);
    }
    return () => { if (user) stopBroadcasting(user.uid); };
  }, [user, mode, isOnline, trackingDeliveryId]);

  useEffect(() => {
    if (googleLoaded) renderMarkers();
  }, [renderMarkers, googleLoaded]);

  const closeDrawer = () => { setAppState('intro'); };

  const handleToggleOnline = () => {
    if (!user?.courierInfo) {
      alert('כדי לקבל משלוחים צריך קודם להירשם כשליח דרך הפרופיל.');
      setAppState('profile');
      return;
    }
    setIsOnline((v) => !v);
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setAppState('intro');
    if (newMode === 'sender') setIsOnline(false);
  };

  if (initializing) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <div className="text-center animate-bounce">
          <div className="bg-blue-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-xl -rotate-6">
            <Truck className="text-white" size={40} />
          </div>
          <h1 className="text-2xl font-black tracking-tight text-gray-800">דרייבר.ישראל</h1>
          <p className="text-gray-400 text-sm">טוען...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <div id="google-map" ref={mapRef}></div>

      {/* --- מסך פתיחה --- */}
      {!user && appState === 'welcome' && (
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm z-50 flex flex-col justify-end">
          <div className="bg-white rounded-t-[30px] p-8 pb-32 shadow-2xl animate-in slide-in-from-bottom">
            <div className="text-center mb-6">
              <div className="bg-blue-600 w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg -rotate-6">
                <Truck className="text-white" size={30} />
              </div>
              <h1 className="text-4xl font-black mb-1 tracking-tight">דרייבר.ישראל</h1>
              <p className="text-blue-600 font-bold text-sm uppercase tracking-wide">משלוחים מהירים בקהילה</p>
            </div>

            <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 mb-6 text-center">
              <p className="text-gray-600 text-sm leading-relaxed">
                צריך לשלוח חבילה? רוצה להרוויח כשליח?
                <b> דרייבר.ישראל מחבר בין שולחים לשליחים באזורך — מהר, פשוט והוגן.</b>
              </p>
            </div>

            <div className="mb-4 flex items-center justify-center gap-2" dir="rtl">
              <button onClick={() => setIsTermsAgreed(!isTermsAgreed)} className="text-gray-600">
                {isTermsAgreed ? <CheckSquare className="text-blue-600" size={24} /> : <Square className="text-gray-400" size={24} />}
              </button>
              <span className="text-sm text-gray-600">
                אני מאשר שקראתי את <button onClick={() => setShowTermsModal(true)} className="text-blue-600 font-bold underline">תקנון האתר</button> ומסכים לתנאים
              </span>
            </div>

            <button onClick={() => setAppState('auth')} disabled={!isTermsAgreed}
              className={`w-full py-4 rounded-xl font-bold flex items-center justify-center gap-3 shadow-xl transition-transform ${
                isTermsAgreed ? 'bg-black text-white active:scale-95' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}>
              <LogIn size={20} /> כניסה / הרשמה
            </button>
            {showTermsModal && <TermsModal onClose={() => setShowTermsModal(false)} />}
          </div>
        </div>
      )}

      {!user && appState === 'auth' && (
        <AuthWizard
          onLoginSuccess={(u) => { setUser(u); setAppState('intro'); }}
          onNewUser={(u) => { setUser(u); setAppState('intro'); }}
          onBack={() => setAppState('welcome')}
        />
      )}

      {user && (
        <div className="floating-ui">

          {/* כפתור תמיכה */}
          <button onClick={() => setShowSupport(true)} className="absolute top-28 right-6 bg-white text-gray-600 p-2 rounded-full shadow-lg z-20 border border-gray-100" title="תמיכה">
            <HelpCircle size={24} />
          </button>
          {showSupport && (
            <div className="absolute inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
              <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden" dir="rtl">
                <SupportForm user={user} onClose={() => setShowSupport(false)} onSuccess={() => setShowSupport(false)} />
              </div>
            </div>
          )}

          {/* כפתור פרופיל */}
          <button onClick={() => setAppState('profile')} className="absolute top-12 right-6 bg-white p-1 rounded-full shadow-xl border-2 border-white z-20">
            {user.profileImage ? <img src={user.profileImage} className="w-10 h-10 rounded-full object-cover" /> : <Menu size={24} />}
          </button>

          {/* כפתור אדמין */}
          {user.uid === "phhsvfx3Q8M7EPBMN8nAE23kM0W2" && (
            <button onClick={() => setAppState('admin')} className="absolute top-12 left-6 bg-black text-white p-2 rounded-full shadow-xl z-20 border-2 border-white" title="ניהול">
              <ShieldCheck size={20} />
            </button>
          )}

          {/* באנר עליון */}
          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 w-[90%] max-w-sm pointer-events-auto z-10">
            <div className="shadow-2xl rounded-2xl overflow-hidden border border-white/50"><AdBanner type="banner" /></div>
          </div>

          {/* --- מתג מצב נוסע/שליח --- */}
          {appState === 'intro' && (
            <div className="absolute bottom-44 left-1/2 transform -translate-x-1/2 w-[90%] max-w-sm pointer-events-auto z-50">
              <div className="bg-white rounded-2xl shadow-xl p-1 flex border border-gray-100">
                <button onClick={() => switchMode('sender')} className={`flex-1 py-2.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all ${mode === 'sender' ? 'bg-blue-600 text-white' : 'text-gray-500'}`}>
                  <Package size={16} /> שולח
                </button>
                <button onClick={() => switchMode('courier')} className={`flex-1 py-2.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all ${mode === 'courier' ? 'bg-black text-white' : 'text-gray-500'}`}>
                  <Truck size={16} /> שליח
                </button>
              </div>
            </div>
          )}

          {/* --- כפתור פעולה ראשי --- */}
          {appState === 'intro' && (
            <div className="absolute bottom-24 left-1/2 transform -translate-x-1/2 w-[90%] max-w-sm pointer-events-auto z-50 animate-in slide-in-from-bottom-4">
              {mode === 'sender' ? (
                <button onClick={() => setAppState('create-delivery')} className="w-full bg-blue-600 text-white py-4 rounded-2xl shadow-xl shadow-blue-200 font-bold text-lg flex items-center justify-center gap-2 active:scale-95 transition-transform">
                  <Package size={24} /> שלח חבילה
                </button>
              ) : (
                <button onClick={() => { if (!isOnline) handleToggleOnline(); setAppState('courier-list'); }} className="w-full bg-black text-white py-4 rounded-2xl shadow-xl font-bold text-lg flex items-center justify-center gap-2 active:scale-95 transition-transform">
                  <Power size={24} /> {isOnline ? 'משלוחים זמינים' : 'התחל לקבל משלוחים'}
                </button>
              )}
            </div>
          )}

          {/* רקע כהה למגירות */}
          {(appState !== 'intro') && <div className="backdrop pointer-events-auto" onClick={closeDrawer}></div>}

          {/* טופס שליחת חבילה */}
          {appState === 'create-delivery' && (
            <div className="side-drawer pointer-events-auto">
              <DeliveryRequestForm userProfile={user} onClose={closeDrawer} onSuccess={closeDrawer} />
            </div>
          )}

          {/* רשימת משלוחים לשליח */}
          {appState === 'courier-list' && (
            <CourierJobList
              currentUser={user}
              courierLocation={userLocation}
              isOnline={isOnline}
              onToggleOnline={handleToggleOnline}
              onClaimed={(id) => { setTrackingDeliveryId(id); setTrackingRole('courier'); setAppState('active-delivery'); }}
              onClose={() => setAppState('intro')}
            />
          )}

          {/* מסך משלוח פעיל (לשני הצדדים) */}
          {appState === 'active-delivery' && trackingDeliveryId && (
            <div className="side-drawer pointer-events-auto">
              <ActiveDelivery
                deliveryId={trackingDeliveryId}
                role={trackingRole}
                onClose={() => { setTrackingDeliveryId(null); setCourierLiveLocation(null); setAppState('intro'); }}
                onCourierLocationUpdate={setCourierLiveLocation}
              />
            </div>
          )}

          {/* באנר "יש לך משלוח פעיל" לשולח */}
          {mode === 'sender' && myActiveDelivery && appState === 'intro' && (
            <div className="absolute bottom-44 left-1/2 transform -translate-x-1/2 w-[90%] max-w-sm pointer-events-auto z-40">
              <button
                onClick={() => { setTrackingDeliveryId(myActiveDelivery.id); setTrackingRole('sender'); setAppState('active-delivery'); }}
                className="w-full bg-green-600 text-white py-3 rounded-xl font-bold shadow-lg flex items-center justify-center gap-2 animate-pulse"
              >
                <Truck size={18} /> יש לך משלוח פעיל — עקוב
              </button>
            </div>
          )}

          {/* פרופיל */}
          {appState === 'profile' && (
            <div className="side-drawer pointer-events-auto h-full overflow-y-auto bg-white">
              <button onClick={closeDrawer} className="absolute top-4 left-4 p-2 bg-gray-100 rounded-full z-20"><X size={20} /></button>
              <Profile userData={user} isOnline={isOnline} onToggleOnline={handleToggleOnline} onClose={closeDrawer} />
            </div>
          )}

          {/* אדמין */}
          {appState === 'admin' && (
            <div className="side-drawer pointer-events-auto h-full overflow-y-auto w-full max-w-4xl bg-white z-50">
              <AdminPanel onClose={() => setAppState('intro')} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
